import React, { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import queryString from 'query-string'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { useIsContentSearchActive } from 'src/Utils/search/search.hooks'
import { useSearchContext } from 'src/Utils/search/search.context'
import {
  ControlsAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { IsGatekeeperEnabled, GatekeeperFlag } from 'src/context/Gatekeeper'
import { generatePermalinkFromId } from '../../../components/PageLayout/Permalink'
import { useControl } from '../../../components/async/model'
import { UrlFragments } from '../../../Utils/globalEnums'
import { ControlGridRow } from './ControlsListItem.styles'
import {
  ControlsListItemOwnerColumn,
  ControlsListItemContentMatchColumn,
  ControlsListItemControlNameColumn,
  ControlsListItemDepartmentColumn,
  ControlsListItemEvidenceColumn,
  ControlsListItemFrameworksColumn,
  ControlsListItemMenuColumn,
  ControlsListItemModelIdColumn,
  ControlsListItemStatusColumn,
  ControlsListItemAIControlCheckColumns,
  LabelCountColumn,
} from './ControlsListItem.components'
import { ControlsListItemProps } from './ControlListItem.constants'
import { getControlsListItemConfig } from './ControlsListItem.helpers'

export const ControlsListItem = ({
  controlModelId,
  audit,
  config,
  linkedPolicyNames = [],
  linkedDepartmentNames = [],
  showContentMatch,
}: ControlsListItemProps): JSX.Element => {
  const isContentSearchActive = useIsContentSearchActive()
  const { contentMatchIds } = useSearchContext()
  const params = useParams()
  const { data, isLoading, mutate } = useControl(controlModelId)

  const isContentMatch = useMemo(
    () => contentMatchIds.has(data?.getId() || ''),
    [contentMatchIds, data],
  )
  const canViewAuditScans = useCanPerformAction(CAPABILITY.CAN_VIEW_AUDIT_SCANS)
  const showLabels = IsGatekeeperEnabled(GatekeeperFlag.LABELS)

  if (!data || isLoading) {
    return <></>
  }

  const control = data.toObject()
  const {
    id: controlId,
    modelId,
    departmentId,
    complianceFrameworkIdsList,
    status,
    policyId: policyModelId,
    migratedControlId,
  } = control

  const isNotApplicable = status === CONTROL_STATUS.NOTAPPLICABLE

  const isFromControlsIndex = location.pathname.includes(
    ControlsAbsoluteRoutes.INDEX,
  )
  const isFromPolicyShow = location.pathname.includes(
    PoliciesAbsoluteRoutes.SHOW,
  )
  const includeSearchParams = isFromControlsIndex || isFromPolicyShow
  const policyFilterFromUrl = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })[FilterParam.POLICY]
  const activePolicyFilter = Array.isArray(policyFilterFromUrl)
    ? policyFilterFromUrl[0]
    : policyFilterFromUrl
  const search = isFromPolicyShow
    ? {
        [FilterParam.POLICY]: [activePolicyFilter || policyModelId],
      }
    : undefined

  let toLink = generatePermalinkFromId({
    pageContext: params.pageContext as string,
    modelId,
    modelType: MODEL_TYPE.CONTROL,
    isInternalLink: true,
    includeSearchParams,
    search,
  })

  if (isFromPolicyShow) {
    toLink += `#${UrlFragments.POLICIES}`
  }

  const {
    showDepartmentColumn,
    showModelIdColumn,
    showControlNameColumn,
    showFrameworkColumn,
    showOperatingEffectivenessColumn,
    showCPMDColumn,
    showLinkedEvidenceColumn,
    showStatusColumn,
    showOwnerColumn,
    showMenuColumn,
  } = getControlsListItemConfig(config)

  return (
    <ControlGridRow
      id={`controls-index-item-${modelId}`}
      as={Link}
      to={toLink}
      $isNotApplicable={isNotApplicable}
    >
      {(showContentMatch ?? (isFromControlsIndex && isContentSearchActive)) && (
        <ControlsListItemContentMatchColumn isContentMatch={isContentMatch} />
      )}
      {showDepartmentColumn && (
        <ControlsListItemDepartmentColumn
          controlId={controlId}
          show={!isNotApplicable}
          departmentId={departmentId}
          departmentNames={linkedDepartmentNames}
        />
      )}
      {showModelIdColumn && <ControlsListItemModelIdColumn modelId={modelId} />}
      {showControlNameColumn && (
        <ControlsListItemControlNameColumn
          control={control}
          isControlNa={isNotApplicable}
          policyNames={linkedPolicyNames}
        />
      )}
      {showLabels && <LabelCountColumn control={control} />}
      {showFrameworkColumn && (
        <ControlsListItemFrameworksColumn
          audit={audit}
          complianceFrameworkIds={complianceFrameworkIdsList}
        />
      )}
      {showLinkedEvidenceColumn && (
        <ControlsListItemEvidenceColumn
          show={!isNotApplicable}
          control={control}
        />
      )}

      {canViewAuditScans && (
        <ControlsListItemAIControlCheckColumns
          controlId={controlId}
          isNotApplicable={isNotApplicable}
          migratedControlId={migratedControlId}
          showOE={showOperatingEffectivenessColumn}
          showCPMD={showCPMDColumn}
        />
      )}
      {showStatusColumn && (
        <ControlsListItemStatusColumn control={control} mutate={mutate} />
      )}
      {showOwnerColumn && (
        <ControlsListItemOwnerColumn control={control} mutate={mutate} />
      )}
      {showMenuColumn && (
        <ControlsListItemMenuColumn control={control} config={config} />
      )}
    </ControlGridRow>
  )
}
